import type { Duration } from 'date-fns'
import { formatDuration as formatDurationBase } from 'date-fns'

interface FormatDurationOptions {
  /**
   * Whether to use long-form units (e.g. "days" instead of "d").
   * @default false
   */
  long?: boolean
  /**
   * Number of digits to show for milliseconds.
   * Milliseconds are only shown if seconds < 30 and no other units are shown.
   * @default 3
   */
  digits?: number
}

/**
 * Formats a duration in seconds into a human-readable string.
 * @param diff - Duration in seconds
 * @param options - options
 */
function formatDuration(diff: number, options: FormatDurationOptions = {}): string {
  // eslint-disable-next-line prefer-const
  let { long = false, digits = 3 } = options

  let seconds = diff
  let minutes = Math.floor(seconds / 60)
  seconds %= 60
  let hours = Math.floor(minutes / 60)
  minutes %= 60
  const days = Math.floor(hours / 24)
  hours %= 24

  const showMilliseconds = diff < 30

  // format decides which units to include in the output
  // assumes we never show weeks, months or years
  const format: (keyof Duration)[] = ['days']

  if (hours > 0 && days < 2) {
    format.push('hours')
  }

  if (minutes > 0 && days === 0 && hours < 12) {
    format.push('minutes')
  }

  if (!showMilliseconds) {
    seconds = Math.floor(seconds)
    format.push('seconds')
  }

  let formattedDuration = formatDurationBase(
    { seconds, minutes, hours, days },
    { format, delimiter: long ? ', ' : ' ' },
  )

  // date-fns doesn't support milliseconds as they are not
  // in the ISO standard for durations so we add them ourselves
  if (showMilliseconds) {
    const secondsWithMilliseconds = seconds.toFixed(digits)

    formattedDuration += formattedDuration.length > 0 ? ' ' : ''
    formattedDuration += secondsWithMilliseconds
    formattedDuration += long ? ' seconds' : 's'
  }

  if (long) return formattedDuration

  const shortDuration = formattedDuration
    .replace(/ days?/, 'd')
    .replace(/ hours?/, 'h')
    .replace(/ minutes?/, 'm')
    .replace(/ seconds?/, 's')

  return shortDuration
}

export { formatDuration }
